import { useEffect, useState } from 'react'

import { CloseIcon } from './Icons'
import {
  DEFAULT_STAMP_TEXT,
  STAMP_FONTS,
  StampPreview,
} from './StampStep'
import { useTranslation } from '@/lib/i18n'
import type { StampStyle } from '@/lib/useCustomizeDraft'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/select'

/** What the workshop can cut along the base. */
export const STAMP_LIMIT = 16

/** Ink colours offered as one-tap swatches, as in the configurator. */
const SWATCHES = ['#FFFFFF', '#0A0A0A', '#EF6CB8', '#22C55E', '#FACC15', '#EF4444']

/**
 * The engraving step, on its own.
 *
 * Customize on the product page stops here rather than walking the
 * customer into the configurator: the popup holds the stamp and its
 * hand and ink, nothing commits anywhere, and dismissing it leaves the
 * page as it was.
 */
export function StampDialog({
  open,
  onOpenChange,
  text,
  style,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  text: string
  style: StampStyle
  onSave: (text: string, style: StampStyle) => void
}) {
  const { t } = useTranslation()
  const [draftText, setDraftText] = useState(DEFAULT_STAMP_TEXT)
  const [draftStyle, setDraftStyle] = useState<StampStyle>(style)

  // Re-seed each time it opens: from what the page already carries, or
  // from the default text so the preview is never blank.
  useEffect(() => {
    if (!open) return
    setDraftText(text || DEFAULT_STAMP_TEXT)
    setDraftStyle(style)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => {
    const next = draftText.trim().slice(0, STAMP_LIMIT)
    if (!next) return
    onSave(next, draftStyle)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[420px]">
        <div className="relative shrink-0">
          <StampPreview
            text={draftText || DEFAULT_STAMP_TEXT}
            style={draftStyle}
            className="h-[210px] max-h-[28dvh] w-full [container-type:inline-size]"
          />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={t('product.stamp.close')}
            className="absolute right-3.5 top-3.5 flex size-8 cursor-pointer items-center justify-center rounded-full border border-[rgba(255,255,255,0.12)] bg-[rgba(10,10,10,0.75)] text-white"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <DialogBody>
          {/* No title: the preview above says what this popup is
              about better than a line of type could. */}
          <p className="text-[14px] leading-relaxed text-ink-muted">{t('product.stamp.note')}</p>

          <Field
            label={t('product.stamp.text_label')}
            htmlFor="pdp-stamp-text"
            counter={`${draftText.length}/${STAMP_LIMIT}`}
          >
            <Input
              id="pdp-stamp-text"
              value={draftText}
              maxLength={STAMP_LIMIT}
              placeholder="SICK"
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  save()
                }
              }}
            />
          </Field>

          <Field label={t('product.stamp.font_label')} htmlFor="pdp-stamp-font">
            <NativeSelect
              id="pdp-stamp-font"
              value={draftStyle.font}
              onChange={(e) => setDraftStyle({ ...draftStyle, font: e.target.value })}
            >
              {STAMP_FONTS.map((font) => (
                <option key={font.key} value={font.key}>
                  {font.label}
                </option>
              ))}
            </NativeSelect>
          </Field>

          <Field label={t('product.stamp.color_label')}>
            <div className="-my-1 flex items-center gap-2.5 overflow-x-auto px-1 py-1">
              {SWATCHES.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  aria-label={swatch}
                  aria-pressed={draftStyle.color.toUpperCase() === swatch}
                  onClick={() => setDraftStyle({ ...draftStyle, color: swatch })}
                  className={cn(
                    'size-8 shrink-0 cursor-pointer rounded-full border',
                    draftStyle.color.toUpperCase() === swatch
                      ? 'border-ink shadow-[0_0_0_2px_var(--sss-surface),0_0_0_4px_var(--sss-ink)]'
                      : 'border-hairline'
                  )}
                  style={{ background: swatch }}
                />
              ))}
            </div>
          </Field>

          <Button variant="primary" size="block" disabled={!draftText.trim()} onClick={save}>
            {t('product.stamp.save')}
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
